import $ from '@rover/globals/jquery';
import _ from '@rover/globals/underscore';
import Backbone from '@rover/globals/backbone';
import Rover from '@rover/globals/Rover';

import SitterCardView from '../sitter_card_view';
import SitterCardQuickView from '../sitter_card_quick_view';
import DateConstraintSuggestionView from '../date_constraint_suggestion_view';

export default Backbone.View.extend({
  // A view that contains all of the sitter cards.  It takes SearchResults
  // as its model.  All of the HTML for this is rendered on the server
  // and is stored in the 'html' attribute on the model.

  events: {
    'click .js-dilla-show-outsiders': 'revealOutsiders',
  },

  el: '.js-search-results-view',

  initialize() {
    // Update the view when the model changes
    this.model.on('change', _.bind(this.onSearchResultsChanged, this));

    // Make things transparent and scroll to top on new search
    Rover.pubSub.on('search:start', _.bind(this.onSearchStart, this));

    // Handle switching between quick views
    Rover.pubSub.on('quickView:previous', _.bind(this.onQuickViewPrevious, this));
    Rover.pubSub.on('quickView:next', _.bind(this.onQuickViewNext, this));

    // Handle scrolling on selection and model change
    Rover.pubSub.on('scroll:topOfResults', _.bind(this.onScrollTopOfResults, this));

    this.setUpChildViews();
  },

  setUpChildViews() {
    this.setUpDateConstraintSuggestionView();
    this.setUpSitterCardViews();
  },

  setUpDateConstraintSuggestionView() {
    if (this.$('.js-date-constraint-suggestion').length) {
      this.dateConstraintSuggestionView = new DateConstraintSuggestionView({
        el: this.$('.js-date-constraint-suggestion'),
      });
    } else {
      this.dateConstraintSuggestionView = null;
    }
  },

  onSearchStart(params, changed) {
    const $sidebarAdvancedFilters = $('.js-advanced-search-filters');
    if ($sidebarAdvancedFilters.length) {
      // Don't scroll to top of results if the only changes are
      // to advanced filters
      const changedParams = Object.keys(changed);
      const advancedFilterNames = $sidebarAdvancedFilters
        .find('input[type="checkbox"]')
        .map((i, el) => $(el).data('attribute-name'))
        .get();
      if (changedParams.every(p => advancedFilterNames.includes(p))) {
        return;
      }
    }

    const scrollTop = $(window).scrollTop();
    const { top } = $('.js-search-results-view').offset();
    const bottom = 0;
    // Check to see if top is out of view
    if (top - scrollTop < bottom) {
      this.onScrollTopOfResults(top);
    }
  },

  onSearchResultsChanged() {
    // Remove the old child views
    this.destroyChildViews();

    // Set the new html
    this.$el.html(this.model.get('html'));

    // Set up the child views
    this.setUpChildViews();
  },

  onScrollTopOfResults(value) {
    $('html, body').animate(
      {
        scrollTop: value - 0,
      },
      this.scrollOptions
    );
  },

  destroyChildViews() {
    this.destroyDateConstraintSuggestionView();
    this.destroySitterCardViews();
  },

  destroyDateConstraintSuggestionView() {
    if (this.dateConstraintSuggestionView) {
      this.dateConstraintSuggestionView.destroyView();
      this.dateConstraintSuggestionView = null;
    }
  },

  destroySitterCardViews() {
    let view = null;
    while (this.sitterCardViews.length > 0) {
      view = this.sitterCardViews.pop();
      view.destroyView();
    }
    while (this.sitterCardQuickViews.length > 0) {
      view = this.sitterCardQuickViews.pop();
      view.destroyView();
    }
  },

  setUpSitterCardViews() {
    this.sitterCardViews = [];
    this.sitterCardQuickViews = [];

    if (!this.model.get('results')) {
      return;
    }

    // set up sitter card views for main search results
    this.setUpSitterCardViewsForType(
      this.$('.js-search-results'),
      this.model.get('query').service_type,
      this.model.get('results'),
      this.model.allResultsCollection,
      0
    );

    // set up sitter card views for alternate results
    const alternateServiceType = this.model.get('alternate_service_type');
    const offset = this.model.get('result_count');
    if (alternateServiceType) {
      this.setUpSitterCardViewsForType(
        this.$('.js-alternate-search-results'),
        alternateServiceType,
        this.model.get('alternate_results'),
        this.model.allResultsCollection,
        offset
      );
    }

    window.util.async_image_loader.load();
  },

  setUpSitterCardViewsForType($el, serviceType, results, allResults, offset) {
    // Set up the sitter cards
    _.each($el.find('.js-sitter-card'), (el, index) => {
      const view = new SitterCardView({
        el: $(el),
        model: allResults.findWhere({ service_pk: results[index].service_pk }),
        service_type: serviceType,
        rank: offset + index + 1,
      });
      this.sitterCardViews.push(view);
    });

    // Set up the sitter card quick views
    _.each($el.find('.js-sitter-card-quick-view'), (el, index) => {
      const view = new SitterCardQuickView({
        el: $(el),
        model: allResults.findWhere({ service_pk: results[index].service_pk }),
        service_type: serviceType,
        rank: offset + index + 1,
      });
      this.sitterCardQuickViews.push(view);
    });
  },

  onQuickViewPrevious(view) {
    let index = this.sitterCardQuickViews.indexOf(view);
    index -= 1;
    if (index < 0) {
      index = this.sitterCardQuickViews.length - 1;
    }

    const previousView = this.sitterCardQuickViews[index];
    this.setQuickViewTransition('prev', previousView.$el);
    this.switchQuickViews(view, previousView);
  },

  onQuickViewNext(view) {
    let index = this.sitterCardQuickViews.indexOf(view);
    index += 1;
    if (index === this.sitterCardQuickViews.length) {
      index = 0;
    }

    const nextView = this.sitterCardQuickViews[index];
    this.setQuickViewTransition('next', nextView.$el);
    this.switchQuickViews(view, nextView);
  },

  switchQuickViews(older, newer) {
    older.$modal.modal('hide');
    newer.$modal.modal('show').trigger('focus');
  },

  /**
   * Sets the appropriate visual transition for a quickview change
   * @param {str} direction 'prev'|'next'
   * @param {jQuery obj} $modal The modal being transitioned to
   */
  setQuickViewTransition: (direction, $modal) => {
    const $modalContent = $modal.find('.modal-content');
    const animationClassPrev = 'slideRight';
    const animationClassNext = 'slideLeft';
    let animationClass;

    // Bail if there's no modal-content el
    if (!$modalContent.length) {
      return;
    }

    // Set appropriate animation for direction
    if (direction === 'prev') {
      animationClass = animationClassPrev;
    } else if (direction === 'next') {
      animationClass = animationClassNext;
    }

    // Add direction animation classes
    $modalContent
      .removeClass(`${animationClassPrev} ${animationClassNext}`)
      .addClass(animationClass);
  },

  revealOutsiders(e) {
    e.preventDefault();
    this.$('.dilla-disabled').removeClass('dilla-disabled');
    Rover.pubSub.trigger('dilla:reveal-outsiders');
    return false;
  },
});



// WEBPACK FOOTER //
// ./js/backbone/views/search/search_results_view.js